import { Notification } from "@bbb/model";
import { ViewTypes } from "@types";

type NotificationNavigationTarget = {
  view: ViewTypes;
  communityId?: string;
  campaignId?: string;
};

type CampaignCommunityIndex = Record<string, string>;

function parseActionUrl(actionUrl?: string) {
  if (!actionUrl) {
    return null;
  }

  if (!actionUrl.startsWith("/") || actionUrl.startsWith("//")) {
    return null;
  }

  try {
    return new URL(actionUrl, "http://localhost");
  } catch {
    return null;
  }
}

export function buildCampaignCommunityIndex(input: Record<string, { campaigns: { id: string }[] }>) {
  return Object.entries(input).reduce((acc, [communityId, { campaigns }]) => {
    campaigns.forEach((campaign) => {
      acc[campaign.id] = communityId;
    });
    return acc;
  }, {} as CampaignCommunityIndex);
}

export function resolveNotificationNavigation(
  notification: Notification,
  campaignCommunityIndex: CampaignCommunityIndex = {},
): NotificationNavigationTarget | null {
  const parsed = parseActionUrl(notification.actionUrl);
  const metadataCommunityId = notification.metadata?.communityId;
  const metadataCampaignId = notification.metadata?.campaignId;

  if (parsed) {
    const segments = parsed.pathname.split("/").filter(Boolean);

    if (segments[0] === "leaderboards") {
      const scope = parsed.searchParams.get("scope");
      const communityId =
        parsed.searchParams.get("communityId") ?? metadataCommunityId ?? undefined;
      const campaignId =
        parsed.searchParams.get("campaignId") ?? metadataCampaignId ?? undefined;

      return {
        view: "leaderboard",
        communityId: scope === "community" || scope === "campaign" ? communityId : undefined,
        campaignId: scope === "campaign" ? campaignId : undefined,
      };
    }

    if (segments[0] === "communities" && segments[1]) {
      const communityId = segments[1];

      if (segments[2] === "campaigns" && segments[3]) {
        return {
          view: "detail",
          communityId,
          campaignId: segments[3],
        };
      }

      if (segments[2] === "leaderboard") {
        return {
          view: "leaderboard",
          communityId,
        };
      }

      return {
        view: "dashboard",
        communityId,
      };
    }

    if (segments[0] === "campaigns" && segments[1]) {
      const campaignId = segments[1];
      const communityId =
        metadataCommunityId ?? campaignCommunityIndex[campaignId];

      if (communityId) {
        return {
          view: segments[2] === "leaderboard" ? "leaderboard" : "detail",
          communityId,
          campaignId,
        };
      }
    }
  }

  if (metadataCampaignId) {
    const communityId = metadataCommunityId ?? campaignCommunityIndex[metadataCampaignId];
    if (communityId) {
      return {
        view: "detail",
        communityId,
        campaignId: metadataCampaignId,
      };
    }
  }

  return null;
}
